import React, { useEffect, useRef, useState } from 'react';
import {
  Fade,
  FormControl,
  FormControlLabel,
  FormLabel,
  makeStyles,
  Radio,
  RadioGroup,
  ThemeProvider,
  Tooltip
} from '@material-ui/core';
import { defineMessages, injectIntl } from 'react-intl';
import CssBaseline from '@material-ui/core/CssBaseline';
import { theme } from 'theme';
import { constants as c, hooks, signaling } from '@lifesize/clients.sdk';
import { closeInCallModal, openInCallModal } from 'actions/InCallModal';
import { useDispatch, useSelector } from 'react-redux';
import Modals from 'constants/Modals';
import get from 'lodash/get';
import { colors } from '@lifesize/ux-assets';

const messages = defineMessages({
  sharingLabel: { id: 'components.inCall.label.sharing', defaultMessage: 'Sharing' },
  allParticipants: { id: 'components.inCall.sharingType.allParticipants', defaultMessage: 'All Participants' },
  limitSharing: { id: 'components.inCall.sharingType.limitSharing', defaultMessage: 'Limit Sharing' },
  limitSharingTooltip: {
    id: 'components.inCall.sharingType.limitSharing.tooltip',
    defaultMessage: 'Only moderators, lecturers, and owners will be allowed to share their screen'
  }
});

interface SharingBlockProps {
  intl: { formatMessage: Function };
  outOfCall: boolean;
}

const SharingBlock = (props: SharingBlockProps) => {
  const { intl } = props;
  const classes = useStyles(props);
  const toolTipClasses = muiToolTipStyle(theme);
  const presentationRestriction = hooks.usePresentationRestriction();
  const [presentationRestrictionOption, setPresentationRestrictionOption] = useState<c.PresentationRestriction>(
    c.PresentationRestriction.None
  );
  const initiatedByCurrentUser = useRef(false);
  const isUpdatingRestriction = hooks.useRequestingPresentationRestriction();
  const media = hooks.useMedia() || {};
  const isRemotePresentationStreamExists = !!media.remotePresentationStream;
  const dispatch = useDispatch();
  const presenter = hooks.usePresentingParticipant() || {};
  const conferenceModerators = hooks.useModerators() || [];
  const conferenceInfo = hooks.useConferenceInfo() || {};
  const inCallModal = useSelector((state) => get(state, 'app.inCallModal'));
  const participants = hooks.useParticipants();

  const isModeratorLecturerOwnerPresenting = () =>
    conferenceModerators.find((moderator) => moderator.uuid === presenter.uuid) ||
    conferenceInfo.lecturerUuid === presenter.uuid ||
    conferenceInfo.ownerUuid === presenter.uuid;

  /*
   * When remote moderation is going on, remotePresentationStream will be empty.
   * This function will check if non Moderator is presenting.
   * */
  const isNonModeratorUserPresenting = () =>
    !isModeratorLecturerOwnerPresenting() && participants.find((participant) => participant.isPresenting);

  const handleRadioButton = (event, value) => {
    setPresentationRestrictionOption(value as c.PresentationRestriction);
    // show stop presentation modal only when remote presentation is going on and presenter is not a moderator or lecturer.
    if (
      (isRemotePresentationStreamExists && !isModeratorLecturerOwnerPresenting()) ||
      (props.outOfCall && isNonModeratorUserPresenting())
    ) {
      dispatch(openInCallModal(Modals.stopPresentation, { initiatedByCurrentUser }));
    } else {
      signaling.modifyRestrictPresentation(value as c.PresentationRestriction);
      initiatedByCurrentUser.current = true;
    }
  };

  useEffect(() => {
    // comparing local value with remote value to make sure that component does not get updated multiple times.
    if (presentationRestriction && presentationRestriction !== presentationRestrictionOption) {
      if (initiatedByCurrentUser.current) {
        // reset initiatedByCurrentUser here because presentationRestriction changed by remote moderator.
        initiatedByCurrentUser.current = false;
      } else {
        // update presentation restriction as remote moderator updated it.
        setPresentationRestrictionOption(presentationRestriction);
      }
    }
  }, [presentationRestriction, presentationRestrictionOption]);

  // Hide Stop presentation modal when other moderator already restricted presentation before current moderator has a chance to restricts it.
  useEffect(() => {
    if (
      inCallModal.visibleComponent === Modals.stopPresentation &&
      presentationRestriction === c.PresentationRestriction.restrictedShare
    ) {
      dispatch(closeInCallModal());
    }
  }, [presentationRestriction, inCallModal.visibleComponent, dispatch]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <FormControl className={classes.sharingBlock} component="fieldset">
        <FormLabel component="legend" focused={false}>
          {intl.formatMessage(messages.sharingLabel)}
        </FormLabel>
        <RadioGroup
          aria-label="sharingType"
          name="sharingType"
          value={presentationRestrictionOption}
          onChange={handleRadioButton}
        >
          <FormControlLabel
            data-testid={'radio-allParticipants'}
            value={c.PresentationRestriction.None as string}
            control={<Radio disabled={isUpdatingRestriction} size={'small'} color="primary" />}
            label={intl.formatMessage(messages.allParticipants)}
          />
          <Tooltip
            classes={{ tooltip: toolTipClasses.tooltip, arrow: toolTipClasses.tooltipArrow }}
            arrow
            placement={'right'}
            title={intl.formatMessage(messages.limitSharingTooltip)}
            TransitionProps={{ timeout: 400 }}
            TransitionComponent={Fade}
          >
            <FormControlLabel
              data-testid={'radio-onlyModerators'}
              value={c.PresentationRestriction.restrictedShare as string}
              control={<Radio disabled={isUpdatingRestriction} size={'small'} color="primary" />}
              label={intl.formatMessage(messages.limitSharing)}
            />
          </Tooltip>
        </RadioGroup>
      </FormControl>
    </ThemeProvider>
  );
};

const muiToolTipStyle = makeStyles(() => ({
  tooltip: {
    fontSize: 14,
    maxWidth: 150,
    backgroundColor: colors.slate
  },
  tooltipArrow: {
    color: colors.slate
  }
}));

const useStyles = makeStyles(() => ({
  sharingBlock: {
    margin: (props: SharingBlockProps) => (props.outOfCall ? theme.spacing(1, 3, 0, 3) : theme.spacing(3, 0, 0, 1)),
    '& legend': {
      color: colors.headlineBlack,
      fontSize: '0.86rem',
      fontWeight: '700',
      margin: '0 0 8px',
      textAlign: (props: SharingBlockProps) => (props.outOfCall ? 'left' : 'inherit')
    },
    '& label': { '& span': { fontSize: '12px' }, '& svg': { fontSize: '1.2rem' } }
  }
}));

export default injectIntl(SharingBlock);
